import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { db } from '../../config/firebase';
import { doc, getDoc } from 'firebase/firestore';

import '../../styles/PublicHistory.css';

import {
  TextField, Button, Typography, Chip, Stack, Box, Paper, CircularProgress, Divider, Snackbar, Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import ShareIcon from '@mui/icons-material/Share';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AssuredWorkloadIcon from '@mui/icons-material/AssuredWorkload';

import Navbar from './Navbar';
import SYNTHETICAImage from '../../assets/SYNTHETICA.webp';

// --- ENHANCED PRINT STYLES (iOS & WebKit Compatible) ---
const printStyles = `
  #print-container {
    display: none;
  }

  @media print {
    @page {
      size: A4;
      margin: 15mm;
    }

    /* Force WebKit (iOS Safari) to respect print backgrounds & colors */
    html, body {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }

    body * {
      visibility: hidden;
    }
    
    nav, .navbar, .search-container, .image-container, .error-text, button, .no-print {
      display: none !important;
    }

    #print-container {
      display: block !important;
      visibility: visible;
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      min-height: 95%;
      margin: 0;
      padding: 40px;
      
      background: white url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='350' height='350'%3E%3Ctext x='50%25' y='50%25' transform='rotate(-45 175 175)' fill='rgba(150,150,150,0.15)' font-size='28' font-family='sans-serif' font-weight='bold' text-anchor='middle'%3Esynthetica-3bf2d%3C/text%3E%3C/svg%3E") repeat center !important;
      -webkit-print-color-adjust: exact !important; 
      print-color-adjust: exact !important;
      
      color: #111 !important;
      font-family: 'Georgia', serif;
      z-index: 9999;
      
      border: 2px solid #222;
      outline: 6px double #222;
      outline-offset: -12px;
    }
    
    #print-container * {
      visibility: visible;
    }

    .print-header {
      text-align: center;
      margin-bottom: 15px;
      margin-top: 20px;
    }
    .print-header h1 {
      font-size: 32px;
      margin: 0;
      text-transform: uppercase;
      letter-spacing: 3px;
      font-weight: 700;
      color: #000;
    }
    .print-header h2 {
      font-size: 14px;
      text-transform: uppercase;
      letter-spacing: 2px;
      color: #555;
      margin-top: 8px;
    }

    .print-link {
      text-align: center;
      margin-bottom: 30px;
      font-family: 'Courier New', Courier, monospace;
      font-size: 13px;
    }
    .print-link a {
      color: #7e5add !important;
      text-decoration: underline;
      word-wrap: break-word;
    }

    .print-meta {
      font-family: 'Courier New', Courier, monospace;
      font-size: 11px;
      color: #333;
      display: flex;
      justify-content: space-between;
      border-top: 1px solid #aaa;
      border-bottom: 1px solid #aaa;
      padding: 12px 0;
      margin-bottom: 30px;
    }
    .print-meta span strong {
      color: #000;
    }

    .print-scores {
      display: flex;
      justify-content: center;
      gap: 50px;
      margin-bottom: 40px;
      padding: 25px;
      background-color: rgba(252, 252, 252, 0.8) !important;
      border: 1px solid #ddd;
      border-radius: 8px;
    }
    .score-box {
      text-align: center;
    }
    .score-value {
      font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
      font-size: 42px;
      font-weight: bold;
      color: #222 !important;
    }
    .score-label {
      font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 1.5px;
      color: #666;
      margin-top: 8px;
      font-weight: bold;
    }

    .print-content {
      line-height: 1.7;
      font-size: 11pt;
      text-align: justify;
      color: #333;
      margin-bottom: 40px;
    }
    .print-content h3 {
      font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
      font-size: 13px;
      text-transform: uppercase;
      letter-spacing: 1px;
      border-bottom: 2px solid #222;
      padding-bottom: 5px;
      margin-bottom: 15px;
      color: #000;
    }
    .print-content p {
      white-space: pre-wrap;
    }

    .print-footer {
      position: absolute;
      bottom: 40px;
      left: 40px;
      right: 40px;
      text-align: center;
      font-size: 10px;
      color: #777;
      font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
      border-top: 1px dashed #ccc;
      padding-top: 15px;
    }
  }
`;

const PublicHistory = () => {
  const { username, tokenid } = useParams();
  const [searchInput, setSearchInput] = useState('');
  const [result, setResult] = useState(null);
  const [currentSearchId, setCurrentSearchId] = useState('');
  const [loading, setLoading] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);

  useEffect(() => {
    if (username && tokenid) {
      setSearchInput(`${username}/${tokenid}`);
      fetchTokenData(username, tokenid);
    }
  }, [username, tokenid]);

  const handleSearchInputChange = (event) => {
    setSearchInput(event.target.value);
  };

  const handleSearch = async () => {
    if (!searchInput.includes('/')) {
      setResult({ error: 'Invalid format. Use "username/token_id".' });
      return;
    }
    
    const [user, tokenId] = searchInput.split('/');
    if (user && tokenId) {
      fetchTokenData(user, tokenId);
    } else {
      setResult({ error: 'Invalid format. Use "username/token_id".' });
    }
  };

  const fetchTokenData = async (user, tokenId) => {
    setLoading(true);
    setResult(null); 
    
    try {
      const tokenDocRef = doc(db, 'users', user, 'tokens', tokenId);
      const tokenDoc = await getDoc(tokenDocRef);

      if (tokenDoc.exists()) {
        setResult(tokenDoc.data());
        setCurrentSearchId(`${user}/${tokenId}`);
      } else {
        setResult({ error: 'No record found for this Token ID.' });
      }
    } catch (e) {
      console.error("Error fetching document: ", e);
      setResult({ error: 'Failed to fetch verification record. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const originalTitle = document.title;
    const formattedFileName = `synthetica-${currentSearchId.replace('/', '-')}`;
    document.title = formattedFileName;
    
    window.print();
    
    setTimeout(() => {
      document.title = originalTitle;
    }, 500);
  };

  // iOS-Friendly Web Share & Clipboard Handler
  const handleShareOrCopy = async () => {
    const shareUrl = `https://synthetica-3bf2d.web.app/token/${currentSearchId}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Synthetica Verification Report',
          text: `Verified Integrity Report for Token ID: ${currentSearchId}`,
          url: shareUrl,
        });
        return;
      } catch (err) {
        // User closed native share sheet
      }
    }

    // Direct synchronous fallback for non-WebShare browsers
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        setToastOpen(true);
        return;
      } catch (err) {
        console.error('Clipboard API error:', err);
      }
    }

    // Fallback for older iOS versions
    const textArea = document.createElement('textarea');
    textArea.value = shareUrl;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      setToastOpen(true);
    } catch (err) {
      console.error('Fallback copy failed:', err);
    } finally {
      document.body.removeChild(textArea);
    }
  };

  const renderScoreDisplay = () => {
    if (!result) return null;

    return (
      <Stack 
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2} 
        justifyContent="center" 
        alignItems="center"
        sx={{ my: { xs: 2, sm: 3 } }}
      >
        {result.accuracy !== undefined && (
          <Chip 
            label={`${Number(result.accuracy).toFixed(2)}% AI-Generated`} 
            variant="outlined" 
            sx={{ 
              fontSize: { xs: '0.9rem', sm: '1.1rem' },
              padding: { xs: 1.5, sm: 2.5 },
              fontWeight: 'bold', 
              color: '#7e5add', 
              borderColor: '#7e5add',
              maxWidth: '100%'
            }} 
          />
        )}
        
        {result.plagiarism !== undefined && (
          <Chip 
            label={`${Number(result.plagiarism).toFixed(2)}% Plagiarised`} 
            color={Number(result.plagiarism) >= 50 ? 'error' : 'success'} 
            variant="outlined" 
            sx={{ 
              fontSize: { xs: '0.9rem', sm: '1.1rem' }, 
              padding: { xs: 1.5, sm: 2.5 }, 
              fontWeight: 'bold',
              maxWidth: '100%' 
            }} 
          />
        )}
      </Stack>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#121212', color: '#ffffff' }}>
      <style>{printStyles}</style>

      {/* --- HIDDEN PRINT REPORT --- */}
      {result && !result.error && (
        <div id="print-container">
            <div className="print-header">
              <h1>Certificate of Analysis</h1>
              <h2>Official Integrity & Originality Report</h2>
            </div>
            
            <div className="print-link">
              <a href={`https://synthetica-3bf2d.web.app/token/${currentSearchId}`} target="_blank" rel="noopener noreferrer">
                https://synthetica-3bf2d.web.app/token/{currentSearchId}
              </a>
            </div>

            <div className="print-meta">
                <span><strong>Record ID:</strong> {currentSearchId}</span>
                <span><strong>Generated On:</strong> {new Date().toLocaleString('en-US', { dateStyle: 'long', timeStyle: 'medium' })}</span>
            </div>

            <div className="print-scores">
                {result.accuracy !== undefined && (
                    <div className="score-box">
                        <div className="score-value">{Number(result.accuracy).toFixed(2)}%</div>
                        <div className="score-label">AI Probability Score</div>
                    </div>
                )}
                {result.plagiarism !== undefined && (
                    <div className="score-box">
                        <div className="score-value">{Number(result.plagiarism).toFixed(2)}%</div>
                        <div className="score-label">Plagiarism Match Score</div>
                    </div>
                )}
            </div>

            <div className="print-content">
              <h3>Analyzed Text Transcript</h3>
              <p>{result.text}</p>
            </div>

            <div className="print-footer">
              <AssuredWorkloadIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
              This document is a certified extract from the Text Integrity System (TIS). Verify at https://synthetica-3bf2d.web.app/token
            </div>
        </div>
      )}

      <Navbar />
      
      <Box sx={{ maxWidth: 800, margin: '0 auto', padding: { xs: 2, sm: 4 } }}>
        <div className="image-container" style={{ textAlign: 'center', marginBottom: '30px' }}>
          <img src={SYNTHETICAImage} alt="SYNTHETICA" style={{ maxWidth: '200px' }} />
        </div>
        
        {/* Search Section */}
        <Paper elevation={0} sx={{ 
          display: 'flex', 
          p: 1, 
          mb: 4, 
          borderRadius: 2, 
          backgroundColor: '#1E1E1E',
          border: '1px solid #333'
        }}>
          <TextField
            fullWidth
            placeholder="username/token_id"
            value={searchInput}
            onChange={handleSearchInputChange}
            variant="outlined"
            size="medium"
            sx={{
              "& .MuiOutlinedInput-root": {
                color: '#fff',
                "& fieldset": { border: 'none' },
                "& input::placeholder": { color: '#888', opacity: 1 }
              }
            }}
          />
          <Button 
            variant="contained" 
            onClick={handleSearch} 
            disabled={loading || !searchInput.trim()}
            sx={{
              backgroundColor: '#7e5add', 
              color: '#fff',
              px: { xs: 2, sm: 4 },
              "&:hover": { backgroundColor: '#6c4bc8' },
              "&.Mui-disabled": { backgroundColor: '#333', color: '#666' }
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : <SearchIcon />}
          </Button>
        </Paper>

        {/* Results Section */}
        {result && (
          <Box className="result-container">
            {result.error ? (
              <Paper elevation={0} sx={{ 
                p: 3, 
                backgroundColor: 'rgba(211, 47, 47, 0.1)', 
                border: '1px solid #d32f2f',
                color: '#ffb4ab', 
                textAlign: 'center', 
                borderRadius: 2 
              }}>
                <Typography variant="h6">{result.error}</Typography>
              </Paper>
            ) : (
              <Paper elevation={0} sx={{ 
                p: { xs: 2, sm: 4 }, 
                borderRadius: 4, 
                backgroundColor: '#1E1E1E',
                border: '1px solid #333',
                color: '#fff'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                  <VerifiedUserIcon sx={{ fontSize: 32, mr: 1, color: '#7e5add' }} />
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    Record Verified
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 2, borderColor: '#333' }} />
                
                {renderScoreDisplay()}
                
                <Box sx={{ 
                  mt: 4, 
                  mb: 4, 
                  p: { xs: 2, sm: 3 }, 
                  backgroundColor: '#121212', 
                  border: '1px solid #333',
                  borderRadius: 2, 
                  maxHeight: 300, 
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': { width: '8px' },
                  '&::-webkit-scrollbar-thumb': { backgroundColor: '#7e5add', borderRadius: '4px' }
                }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#E0E0E0', lineHeight: 1.6 }}>
                    {result.text}
                  </Typography>
                </Box>
                
                {/* Responsive Dual Button Group */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                  <Button 
                    variant="contained" 
                    size="large"
                    startIcon={<PrintIcon />}
                    onClick={handlePrint}
                    sx={{ 
                      backgroundColor: '#7e5add', 
                      color: 'white',
                      borderRadius: 8,
                      px: { xs: 2, sm: 4 },
                      py: 1.5,
                      fontWeight: 'bold',
                      "&:hover": { backgroundColor: '#6c4bc8' }
                    }}
                  >
                    Print / Save PDF
                  </Button>

                  <Button 
                    variant="outlined" 
                    size="large"
                    startIcon={<ShareIcon />}
                    onClick={handleShareOrCopy}
                    sx={{ 
                      borderColor: '#7e5add', 
                      color: '#7e5add',
                      borderRadius: 8,
                      px: { xs: 2, sm: 4 },
                      py: 1.5,
                      fontWeight: 'bold',
                      "&:hover": { borderColor: '#6c4bc8', backgroundColor: 'rgba(126, 90, 221, 0.08)' }
                    }}
                  >
                    Share Report
                  </Button>
                </Stack>
              </Paper>
            )}
          </Box>
        )}
      </Box>

      {/* Copy Toast Notification */}
      <Snackbar 
        open={toastOpen} 
        autoHideDuration={3000} 
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          Link copied to clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PublicHistory;